import type { FacetTableData } from "./facet-table-helpers";
import { resolveEtlNodeFromDocument, writeEtlNodeToDocument } from "./facet-etl-document-helpers";

export function reorderTableRows(table: FacetTableData, rowLabels: string[]): FacetTableData {
    const labelField = table.headers[0];
    if (!labelField || rowLabels.length === 0) return table;
    const pool = [...table.rows];
    const nextRows: Record<string, unknown>[] = [];
    for (const label of rowLabels) {
        const idx = pool.findIndex((row) => String(row[labelField] ?? "") === String(label ?? ""));
        if (idx < 0) continue;
        nextRows.push(pool[idx]);
        pool.splice(idx, 1);
    }
    return { headers: table.headers, rows: [...nextRows, ...pool] };
}

export function reorderTableColumns(table: FacetTableData, allHeaders: string[]): FacetTableData {
    const labelHeader = table.headers[0];
    if (!labelHeader) return table;
    const hasTotal = table.headers.includes("Total");
    const fromGrid = allHeaders.filter((h) => table.headers.includes(h));
    const missing = table.headers.filter((h) => !fromGrid.includes(h));
    let headers = [...fromGrid, ...missing];
    headers = [labelHeader, ...headers.filter((h) => h !== labelHeader)];
    if (hasTotal) {
        headers = [...headers.filter((h) => h !== "Total"), "Total"];
    }
    const rows = table.rows.map((row) => {
        const next: Record<string, unknown> = {};
        for (const h of headers) next[h] = row[h] ?? "";
        return next;
    });
    return { headers, rows };
}

export function labelsToAxisSort(labels: string[]): Record<string, number> {
    const result: Record<string, number> = {};
    labels.forEach((label, idx) => {
        result[String(label ?? "")] = idx;
    });
    return result;
}

/** Data columns only — skips row-label header and optional Total column. */
export function dataColumnHeadersForSort(allHeaders: string[]): string[] {
    return allHeaders.slice(1).filter((h) => h !== "Total");
}

export function isMatrixShapedOp(op: Record<string, unknown>): boolean {
    const type = op.type;
    const rowKey = String(op.row_key ?? "").trim();
    const colKey = String(op.col_key ?? "").trim();
    if (type === "matrix") return Boolean(rowKey) && Boolean(colKey);
    if (type === "pivot") return Boolean(rowKey) && Boolean(colKey);
    return false;
}

function pivotOpIndexInEtlNode(etlNode: Record<string, unknown>): number {
    const ops = etlNode.ops;
    if (!Array.isArray(ops)) return -1;
    for (let i = ops.length - 1; i >= 0; i--) {
        const op = ops[i];
        if (op == null || typeof op !== "object") continue;
        const type = (op as Record<string, unknown>).type;
        if (type === "matrix" || type === "pivot") return i;
    }
    return -1;
}

export type AxisSortValue = Record<string, number> | string[];

export function patchMatrixAxisSort(
    etlJson: Record<string, unknown>,
    patch: { row_sort?: AxisSortValue; column_sort?: AxisSortValue }
): Record<string, unknown> | null {
    const etlNode = resolveEtlNodeFromDocument(etlJson);
    if (!etlNode) return null;
    const opIndex = pivotOpIndexInEtlNode(etlNode);
    if (opIndex < 0) return null;
    const ops = [...(Array.isArray(etlNode.ops) ? etlNode.ops : [])];
    const op = { ...(ops[opIndex] as Record<string, unknown>) };
    const isMatrix = isMatrixShapedOp(op);
    if (patch.row_sort !== undefined) op.row_sort = patch.row_sort;
    if (patch.column_sort !== undefined && isMatrix) op.column_sort = patch.column_sort;
    ops[opIndex] = op;
    return writeEtlNodeToDocument(etlJson, { ...etlNode, ops });
}

export function parseAxisSort(raw: unknown): Record<string, number> {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return {};
    const result: Record<string, number> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        const n = typeof v === "number" ? v : Number(v);
        if (Number.isFinite(n)) result[k] = n;
    }
    return result;
}

export function parseAxisSortValue(raw: unknown): AxisSortValue {
    if (Array.isArray(raw)) {
        return raw.map((item) => String(item ?? ""));
    }
    const parsed = parseAxisSort(raw);
    return Object.keys(parsed).length > 0 ? parsed : {};
}
